
import Page from '@/Page.vue';
import { Component, Prop } from 'vue-property-decorator';
import { shiftsStore, shiftStationsStore } from '@/libs/shifts/+state/store';
import { CrudAction, CrudReponse } from '@/libs/core/+state/models/crud-action';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';
import {
    LocationModel,
    PermissionTypeEnum,
    ShiftModel,
    ShiftStateEnum,
    ShiftStationModel,
    TenantUserModel,
    WorkplaceModel,
} from '@/libs/Api';
import moment from 'moment';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { locationsStore } from '@/libs/locations/+state/store';
import { workplacesStore } from '@/libs/workplaces/+state/store';
import activeModeService from '@/libs/common/services/active-mode-service';
import ShiftState from '@/libs/shifts/components/ShiftState.vue';
import { authStore } from '@/libs/auth/+store/store';
import { AuthGetter } from '@/libs/auth/models/auth-state';

@Component({
    components: { ShiftState },
})
export default class ShiftWorkList extends Page {
    @Prop() tenantId!: string;
    protected ShiftStateEnum: typeof ShiftStateEnum = ShiftStateEnum;
    private redirectInterval: any = null;
    private tenantUser = authStore.useGetter(AuthGetter.GetTenantUser) as TenantUserModel;

    get isLoading(): boolean {
        return shiftsStore.useGetter(CrudGetter.Pending, 'ShiftWorkList') ?? false;
    }

    get shifts(): ShiftModel[] {
        return this.getShiftList();
    }

    mounted() {
        this.loadItems();
        if (this.isLimitedWork()) {
            this.redirectInterval = setInterval(() => {
                this.checkLimitedWork();
            }, 1000);
        }
        this.subscribe(shiftsStore, CrudReponse.GetAll, 'ShiftWorkList').then((e: ShiftModel[]) => {
            if (this.isLimitedWork()) {
                const filteredList = this.getShiftList();
                if (filteredList.length == 1) {
                    this.$router.replace({
                        name: 'ShiftWorkDetail',
                        params: { tenantId: this.tenantId!, id: filteredList[0].id! },
                    });
                }
            } else {
                this.checkLimitedWork();
            }
        });
    }

    destroyed() {
        clearInterval(this.redirectInterval);
    }

    loadItems() {
        shiftsStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload(
                [
                    { field: 'fromDate', index: 1, order: 'desc' },
                    { field: 'workplace.code', index: 2, order: 'asc' },
                ],
                [
                    { field: 'shiftState', op: 'in', comparand: [ShiftStateEnum.Waiting, ShiftStateEnum.Opened] },
                    { field: 'fromDate', op: 'lte', comparand: moment().format('YYYY-MM-DDTHH:mm:ss') },
                ],
                undefined,
                false,
                'ShiftWorkList'
            )
        );
        shiftStationsStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload(
                [{ field: 'id', index: 1, order: 'desc' }],
                [
                    { field: 'shift.shiftState', op: 'in', comparand: [ShiftStateEnum.Waiting, ShiftStateEnum.Opened] },
                    { field: 'shift.fromDate', op: 'lte', comparand: moment().format('YYYY-MM-DDTHH:mm:ss') },
                ],
                undefined,
                false,
                'ShiftWorkList'
            )
        );
    }

    isLimitedWork(): boolean {
        return (
            this.hasPermissions([PermissionTypeEnum.ShiftWorkLimited]) && !this.hasPermissions([PermissionTypeEnum.ShiftWork])
        );
    }

    getShiftList(): ShiftModel[] {
        let items = (shiftsStore.useGetter(CrudGetter.Data, 'ShiftWorkList') ?? []) as ShiftModel[];
        const shiftStations: ShiftStationModel[] = shiftStationsStore.useGetter(CrudGetter.Data, 'ShiftWorkList') ?? [];
        if (
            shiftsStore.useGetter(CrudGetter.Pending, 'ShiftWorkList') == true ||
            shiftStationsStore.useGetter(CrudGetter.Pending, 'ShiftWorkList')
        ) {
            return [];
        }
        items = items.filter((x) => x.shiftState != ShiftStateEnum.Closed);
        const locations: LocationModel[] = locationsStore.useGetter(CrudGetter.Data) ?? [];
        const workplaces: WorkplaceModel[] = workplacesStore.useGetter(CrudGetter.Data) ?? [];
        const activeWorkplaces = activeModeService.getActiveWorkplaces();
        const activeLocations = activeModeService.getActiveLocations();
        const locationScopes = locations
            .filter((x) => x.locationParents!.some((y) => activeLocations.includes(y.parentalLocationId!)))
            .map((x) => x.id!)
            .concat(activeLocations!);
        if (activeWorkplaces.length > 0) {
            items = items.filter((x) => activeWorkplaces.includes(x.workplaceId!));
        } else if (activeLocations.length > 0) {
            const workplacesToFilter = workplaces.filter((x) => locationScopes.includes(x.locationId!)).map((x) => x.id!);
            items = items.filter((x) => workplacesToFilter.includes(x.workplaceId!));
        }
        if (this.isLimitedWork()) {
            const myShifts = shiftStations.filter((x) => x.workerId == this.tenantUser.id).map((x) => x.shiftId!);
            items = items.filter((x) => myShifts.includes(x.id!));
            items = items.filter((x) => x.shiftState == ShiftStateEnum.Opened);
        }
        return items;
    }

    checkLimitedWork() {
        if (this.isLimitedWork()) {
            let shifts = this.getShiftList();
            shifts = shifts.filter((x) => x.shiftState == ShiftStateEnum.Opened);
            if (shifts.length > 0) {
                const runningShifts = shifts.filter(
                    (x) =>
                        new Date(x.fromDate!).getTime() <= new Date().getTime() &&
                        new Date(x.toDate!).getTime() >= new Date().getTime()
                );
                if (runningShifts.length == 1) {
                    const runningShift = runningShifts[0];
                    this.$router.replace({
                        name: 'ShiftWorkDetail',
                        params: { tenantId: this.tenantId!, id: runningShift.id! },
                    });
                }
            }
        }
    }
}
